import { memo } from "react";
import { getDimensionSet, getTireType, getTireManufacturers } from "services/api/fleet/tires/helpers";
import { FleetTireSetEditFormValues } from "services/api/fleet/tires/types";
import Badge from "ui/components/Badge/Badge";
import { transformDateToLocalDateString } from "helpers";
import { DemountFormValues } from "../types";

interface ResolvedDemountNames {
  targetWarehouse?: { name?: string | null; address?: string | null };
  newTireSet?: { code?: string | null; dimension?: string | null; type?: string | null; manufacturer?: string | null };
}

interface DemountSummaryProps {
  currentSet: FleetTireSetEditFormValues;
  formValues: DemountFormValues;
  resolvedNames: ResolvedDemountNames;
}

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-start py-2.5">
    <span className="text-sm text-slate-500 min-w-[160px]">{label}</span>
    <div className="text-sm font-medium text-slate-900 text-right">{children}</div>
  </div>
);

const DemountSummary: React.FC<DemountSummaryProps> = ({ currentSet, formValues, resolvedNames }) => {
  const { targetWarehouse, newTireSet } = resolvedNames;
  const tireType = getTireType(currentSet.tires);
  const dimension = getDimensionSet(currentSet.tires);
  const manufacturers = getTireManufacturers(currentSet.tires);
  const dateStr = formValues.transferDate ? (transformDateToLocalDateString(formValues.transferDate) ?? "-") : "-";

  const vehicleName = currentSet.vehicle?.name
    ? `${currentSet.vehicle.name} ${currentSet.vehicle.registrationPlate ? `[${currentSet.vehicle.registrationPlate}]` : ""}`
    : "";

  return (
    <div className="flex items-center justify-center px-4 min-h-[70vh] xl:min-h-[1000px]">
      <div className="w-full max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">Potvrda demontaže</h1>
        <p className="text-center text-md text-slate-500 mb-8">Provjerite podatke prije izvršenja demontaže.</p>

        <div className="space-y-4">
          {/* Set guma koji se demontira */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 pt-4 pb-1">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Set guma koji se demontira
              </div>
              <div className="divide-y divide-slate-100">
                <InfoRow label="Kod seta"><span className="font-semibold">{currentSet.code}</span></InfoRow>
                <InfoRow label="Dimenzija">{dimension || "-"}</InfoRow>
                <InfoRow label="Tip gume">{tireType?.name ?? "-"}</InfoRow>
                <InfoRow label="Proizvođač">{manufacturers || "-"}</InfoRow>
                <InfoRow label="Broj guma">{currentSet.tires?.length ?? 0}</InfoRow>
                <InfoRow label="Datum demontaže">{dateStr}</InfoRow>
              </div>
            </div>
          </div>

          {/* Demontaža s vozila */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 pt-4 pb-1">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Demontaža s vozila</div>
              <div className="divide-y divide-slate-100">
                <InfoRow label="Skida se s vozila">
                  <div className="flex items-center gap-2">
                    {currentSet.vehicle?.defaultImage && (
                      <img src={currentSet.vehicle.defaultImage} alt="" className="h-6 w-6 object-contain rounded-full border border-slate-200" />
                    )}
                    <span className="font-semibold">{vehicleName || "-"}</span>
                  </div>
                </InfoRow>
                <InfoRow label="Sprema se na skladište">
                  <span className="font-semibold">{targetWarehouse?.name ?? "-"}</span>
                </InfoRow>
                {targetWarehouse?.address && (
                  <InfoRow label="Adresa skladišta">
                    <span className="text-slate-500">{targetWarehouse.address}</span>
                  </InfoRow>
                )}
              </div>
            </div>

            {/* Montaža novog seta na vozilo */}
            {newTireSet?.code && (
              <div className="px-5 pt-3 pb-1 border-t border-slate-200 bg-green-50/50">
                <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2 flex justify-between">
                  <span>Montaža novog seta na vozilo</span>
                  <Badge text="Montira se" variant="success" height="small" />
                </div>
                <div className="divide-y divide-slate-100">
                  <InfoRow label="Novi set">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{newTireSet.code}</span>
                    </div>
                  </InfoRow>
                  {newTireSet.dimension && <InfoRow label="Dimenzija">{newTireSet.dimension}</InfoRow>}
                  {newTireSet.type && <InfoRow label="Tip gume">{newTireSet.type}</InfoRow>}
                  {newTireSet.manufacturer && <InfoRow label="Proizvođač">{newTireSet.manufacturer}</InfoRow>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(DemountSummary);
